import React, { FC, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { Button, Col, Form, message, Row, Tabs } from 'antd';
import { SendOutlined } from '@ant-design/icons';

import ClientVersionSelect from '@lib/components/client-version-select';
import EnvironmentSelect from '@lib/components/env-select';
import BlueprintDataProvider from '@lib/pages/blueprint-data/components/context';
import { BlueprintDataFormRef } from '@lib/pages/blueprint-data/components/form';
import { InboxV3Provider, useInboxContext } from '@lib/pages/cstool/inbox/components/context';
import InboxMessageForm from '@lib/pages/cstool/inbox/components/form';
import { CsInboxHistoryLocation } from '@lib/routes/routes';

import { registerBatchTask } from '@services/cstool/inbox-batch-players';

import InboxBatchRewardsBody from './inbox-batch-rewards';

// Identical to @lib/pages/cstool/inbox/v3's compose page (same message form, same file
// upload) - the only difference is handleSubmit posts to this feature's own chunking-aware
// endpoint instead of the shared one, so a large player list is split across several rows
// instead of overflowing InboxHistory.playerIds's `text` column.
const InboxBatchPlayersBody: FC = () => {
  const history = useHistory();

  const { version } = useInboxContext();

  const formRef = useRef<BlueprintDataFormRef>(null);

  const [environment, setEnvironment] = useState<string>();
  const [blueprintVersion, setBlueprintVersion] = useState<string>();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!formRef.current || !environment) return;

    const formData = await formRef.current?.onSave();
    if (!formData?.file && !formData?.playerId) {
      message.error('Player Id or Player list is required!');
      return;
    }

    setLoading(true);

    const res = await registerBatchTask(version, {
      environment,
      blueprintVersion,
      file: formData.file,
      playerId: formData.playerId,
      data: JSON.stringify(formData.data),
    });
    if (res.ok) {
      const batchCount = Array.isArray(res.data) ? res.data.length : 1;
      message.success(
        batchCount > 1
          ? `Inbox message split into ${batchCount} batches and scheduled to deploy successfully!`
          : 'Inbox message is scheduled to deploy successfully!',
      );

      history.push({
        pathname: CsInboxHistoryLocation.path,
        search: `?environment=${environment}`,
      });
    }

    setLoading(false);
  };

  return (
    <div className="ant-form ant-form-vertical" style={{ maxWidth: '100%' }}>
      <Row>
        <Col flex="0 0 220px">
          <Form.Item label="Environment" rules={[{ required: true }]}>
            <EnvironmentSelect onChange={setEnvironment} />
          </Form.Item>
        </Col>
        <Col flex="0 0 220px">
          <Form.Item label="Client Version" name="blueprintVersion" style={{ marginLeft: 10 }}>
            <ClientVersionSelect environment={environment} onChange={setBlueprintVersion} />
          </Form.Item>
        </Col>
        <Col flex="0 0 220px">
          <div style={{ marginTop: 30, marginLeft: 15 }}>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSubmit}
              disabled={!environment || !blueprintVersion}
              loading={loading}
            >
              Submit
            </Button>
          </div>
        </Col>
        <Col flex="0 0 auto" className="ml-auto">
          <div style={{ marginTop: 30 }}>
            {CsInboxHistoryLocation.toLink(<Button type="dashed">Inbox History</Button>)}
          </div>
        </Col>
      </Row>
      <InboxMessageForm ref={formRef} />
    </div>
  );
};

const InboxBatchPlayersScreen: FC = () => (
  <BlueprintDataProvider>
    <InboxV3Provider>
      <div className="flex p-4">
        <Tabs defaultActiveKey="batch" style={{ width: '100%' }}>
          <Tabs.TabPane tab="Send Inbox Batch" key="batch">
            <InboxBatchPlayersBody />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Send Inbox Batch With Rewards" key="batch-rewards">
            <InboxBatchRewardsBody />
          </Tabs.TabPane>
        </Tabs>
      </div>
    </InboxV3Provider>
  </BlueprintDataProvider>
);

export default InboxBatchPlayersScreen;
